import { useEffect, useState } from "react";
import axios from "axios";
import { usePostalCity, portalLookup, formatCp } from "@/lib/postal";
import { Loader2, CheckCircle2, IdCard } from "lucide-react";
import { Chips, CIVIL, REGIME } from "@/components/BuyerData";
import { fmtDate } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = "w-full rounded-xl border-2 border-slate-200 px-3.5 py-3 text-[16px] outline-none focus:border-slate-400";
const Q = ({ label, children }) => <div className="space-y-2"><div className="text-sm font-semibold text-slate-800">{label}</div>{children}</div>;
const STEPS = ["Cartão de cidadão", "Os seus dados"];
const btn = "w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-semibold bg-ec-green disabled:opacity-50";

function Progress({ step }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="bf-progress">
      {STEPS.map((s, i) => (
        <div key={s} data-state={i < step ? "done" : i === step ? "current" : "todo"}>
          <div className={`h-1.5 rounded-full ${i <= step ? "bg-ec-green" : "bg-slate-200"}`} />
          <div className={`mt-1.5 text-xs ${i === step ? "text-ec-green font-semibold" : "text-slate-400"}`}>{i + 1}. {s}</div>
        </div>
      ))}
    </div>
  );
}

function ReadBox({ read }) {
  const rows = [["Nome", read.name], ["NIF", read.nif], ["N.º do documento", read.number], ["Validade", read.validity && fmtDate(read.validity)]].filter(([, v]) => v);
  if (!rows.length) return <p className="text-sm text-slate-500" data-testid="bf-read-none">Recebemos o seu cartão. Vamos ler os dados e confirmar.</p>;
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3 text-sm space-y-1" data-testid="bf-read">
      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Lido do seu cartão</div>
      {rows.map(([k, v]) => <div key={k}><span className="text-slate-500">{k}:</span> <b className="text-slate-800">{v}</b></div>)}
    </div>
  );
}

// Portal do comprador: «Dados para o CPCV». Primeiro o CC (a IA lê nome, NIF, n.º e validade), depois um formulário curto.
export function BuyerForm({ token, session, form, ccCard, onDone }) {
  const [step, setStep] = useState(form.cc_received ? 1 : 0);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);
  return (
    <div className="space-y-6" data-testid="buyer-form">
      <Progress step={step} />
      {step === 0 ? (
        <div className="space-y-4" data-testid="bf-step-1">
          <div>
            <h2 className="font-heading text-xl text-ec-green">Dados para o contrato-promessa</h2>
            <p className="text-sm text-slate-500 mt-1">Comece pelo seu cartão de cidadão: lemos o nome, o NIF, o número e a validade. Não precisa de escrever nada.</p>
          </div>
          {ccCard}
          <button onClick={() => setStep(1)} disabled={!form.cc_received} className={btn} data-testid="bf-next"><IdCard size={18} /> Continuar</button>
        </div>
      ) : <BuyerStepData token={token} session={session} form={form} onBack={() => setStep(0)} onDone={onDone} />}
    </div>
  );
}

export function BuyerStepData({ token, session, form, onBack, onDone }) {
  const [f, setF] = useState({ civil_status: form.civil_status, regime: form.regime, spouse_name: form.spouse_name, address: form.address,
    postal_code: form.postal_code, locality: form.locality, email: form.email, phone: form.phone });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (v) => setF((o) => ({ ...o, [k]: typeof v === "string" ? v : v.target.value }));
  usePostalCity(f.postal_code, f.locality, set("locality"), portalLookup(token, session));
  const withRegime = ["casado", "uniao_facto"].includes(f.civil_status);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.civil_status) { setErr("Escolha o estado civil."); return; }
    if (withRegime && !f.regime) { setErr("Escolha o regime de bens."); return; }
    if (f.civil_status === "casado" && !(f.spouse_name || "").trim()) { setErr("Indique o nome do cônjuge."); return; }
    if ((f.address || "").trim().length < 3 || (f.locality || "").trim().length < 2) { setErr("Indique a morada e a localidade."); return; }
    if (!/^\s*\d{4}\s*-\s*\d{3}\s*$/.test(f.postal_code || "")) { setErr("Código postal no formato 0000-000."); return; }
    setBusy(true);
    try { await axios.post(`${API}/portal/${token}/buyer-form`, { ...f, regime: withRegime ? f.regime : null }, { headers: { "X-Portal-Session": session } }); onDone(); }
    catch (ex) { const det = ex.response?.data?.detail; setErr(typeof det === "string" ? det : "Verifique os dados e tente de novo."); setBusy(false); }
  };
  return (
    <form onSubmit={submit} noValidate className="space-y-6" data-testid="bf-step-2">
      <div>
        <h2 className="font-heading text-xl text-ec-green">Os seus dados</h2>
        <p className="text-sm text-slate-500 mt-1">Confirme ou complete. É o que vai constar no contrato-promessa.</p>
      </div>
      <ReadBox read={form.read || {}} />
      <Q label="Estado civil"><Chips options={CIVIL} value={f.civil_status} onChange={set("civil_status")} testid="bf-civil" /></Q>
      {withRegime && <Q label="Regime de bens"><p className="text-xs text-slate-500 -mt-1">Se não sabe, escolha Não sei.</p><Chips options={[...REGIME, ["nao_sei", "Não sei"]]} value={f.regime} onChange={set("regime")} testid="bf-regime" /></Q>}
      {f.civil_status === "casado" && <Q label="Nome completo do cônjuge"><input className={inp} value={f.spouse_name} onChange={set("spouse_name")} data-testid="bf-spouse" /></Q>}
      <Q label="Morada de residência">
        <input className={inp} value={f.address} onChange={set("address")} placeholder="Rua, número, andar" data-testid="bf-address" />
        <div className="grid grid-cols-[120px_1fr] gap-2">
          <input className={inp} value={f.postal_code} onChange={(e) => set("postal_code")(formatCp(e.target.value))} placeholder="0000-000" inputMode="numeric" data-testid="bf-cp" />
          <input className={inp} value={f.locality} onChange={set("locality")} placeholder="Localidade" data-testid="bf-locality" />
        </div>
      </Q>
      <Q label="Email"><input type="email" className={inp} value={f.email} onChange={set("email")} data-testid="bf-email" /></Q>
      <Q label="Telemóvel"><input type="tel" className={inp} value={f.phone} onChange={set("phone")} data-testid="bf-phone" /></Q>
      {err && <div className="text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2" data-testid="bf-error">{err}</div>}
      <button type="submit" disabled={busy} className={btn} data-testid="bf-submit">{busy ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />} Enviar</button>
      {onBack && <button type="button" onClick={onBack} className="w-full text-sm text-slate-500 underline" data-testid="bf-back">Voltar ao cartão de cidadão</button>}
    </form>
  );
}

export const thanksLine = (name) => {
  const first = (name || "").split(" ")[0];
  return first ? `Obrigado! ${first.toLowerCase().endsWith("a") ? "A" : "O"} ${first} vai preparar o CPCV.` : "Obrigado! O seu consultor vai preparar o CPCV.";
};
